import React, { useEffect, useRef, useState } from "react";
import {
  Card,
  Typography,
  Avatar,
  Space,
  Input,
  Button,
  Upload,
  Image,
  Modal,
  Form,
  Divider,
  Select,
  message,
  Tooltip,
  Dropdown,
  Checkbox,
} from "antd";
import {
  UserOutlined,
  RobotOutlined,
  ClockCircleOutlined,
  SendOutlined,
  PaperClipOutlined,
  SyncOutlined,
  LikeOutlined,
  DislikeOutlined,
  LikeFilled,
  DislikeFilled,
} from "@ant-design/icons";
import { styles } from "../../styles";
import RenderImageCollage from "./ChatImage";
import { useAppDispatch } from "../../../../hooks/reduxHooks";
import {
  handleFeedbackApi,
  handleFeedbackDeleteApi,
} from "../../../../services/feedbackLog.service";
import FeedbackModal from "./FeedbackModal";
import { filterValidUrls } from "../../../../utils/urlValidator";
import QueryFindingInfoButton from "../../../../components/QueryFindingInfoButton";
import "./index.css";

const { Text, Title, Paragraph } = Typography;
const { TextArea } = Input;

// Format the timestamp
const formatTime = (timestamp) => {
  const date = new Date(timestamp);
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

// Parse reply text with line breaks
const parseReplyText = (text) => {
  return text.split("\n").map((line, index) => (
    <span key={index}>
      {line}
      {index < text.split("\n").length - 1 && <br />}
    </span>
  ));
};

const formatAudioTime = (value = 0) => {
  if (!Number.isFinite(value)) return "0:00";

  const minutes = Math.floor(value / 60);
  const seconds = Math.floor(value % 60);
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
};

const VoiceMessagePlayer = ({ src, isSelected = false }) => {
  const audioRef = useRef(null);
  const playerIdRef = useRef(`voice-${Math.random().toString(36).slice(2)}`);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return undefined;

    const handleTimeUpdate = () => setCurrentTime(audio.currentTime || 0);
    const handleLoadedMetadata = () => setDuration(audio.duration || 0);
    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("ended", handleEnded);

    return () => {
      audio.pause();
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("ended", handleEnded);
    };
  }, [src]);

  useEffect(() => {
    const handleOtherVoicePlay = (event) => {
      if (event.detail?.playerId === playerIdRef.current) return;

      audioRef.current?.pause();
      setIsPlaying(false);
    };

    window.addEventListener("echo-voice-play", handleOtherVoicePlay);
    return () => window.removeEventListener("echo-voice-play", handleOtherVoicePlay);
  }, []);

  const progress = duration ? (currentTime / duration) * 100 : 0;

  const handleTogglePlay = async () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
      return;
    }

    try {
      window.dispatchEvent(
        new CustomEvent("echo-voice-play", {
          detail: { playerId: playerIdRef.current },
        })
      );
      await audio.play();
      setIsPlaying(true);
    } catch (error) {
      message.error("Unable to play this voice message.");
    }
  };

  const handleSeek = (event) => {
    const audio = audioRef.current;
    if (!audio) return;

    const bounds = event.currentTarget.getBoundingClientRect();
    const clickRatio = bounds.width ? (event.clientX - bounds.left) / bounds.width : 0;
    const nextTime = Math.max(0, Math.min(1, clickRatio)) * (duration || 0);
    audio.currentTime = nextTime;
    setCurrentTime(nextTime);
  };

  const waveformBars = [12, 18, 9, 24, 16, 30, 11, 22, 14, 27, 10, 20, 28, 13, 24, 17, 31, 12, 21, 15, 26, 10, 19, 23, 14, 29, 11, 20];

  return (
    <div
      style={{
        width: "270px",
        maxWidth: "100%",
        padding: "9px 11px",
        backgroundColor: isSelected ? "#c6c6c6" : "#ffffff",
        border: "1px solid #e5e7eb",
        borderRadius: "999px",
        boxShadow: "0 4px 14px rgba(15, 23, 42, 0.07)",
      }}
    >
      <audio ref={audioRef} src={src} preload="metadata" />
      <div style={{ display: "flex", alignItems: "center", gap: "9px" }}>
        <Button
          type="primary"
          shape="circle"
          onClick={handleTogglePlay}
          style={{
            width: "32px",
            height: "32px",
            minWidth: "32px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#1677ff",
            boxShadow: "0 4px 10px rgba(22, 119, 255, 0.24)",
          }}
        >
          <iconify-icon
            icon={isPlaying ? "mdi:pause" : "mdi:play"}
            width="18"
            height="18"
            style={{ marginLeft: isPlaying ? 0 : "2px" }}
          />
        </Button>

        <div
          onClick={handleSeek}
          style={{
            flex: 1,
            minWidth: 0,
            height: "34px",
            display: "flex",
            alignItems: "center",
            gap: "3px",
            cursor: duration ? "pointer" : "default",
          }}
        >
          {waveformBars.map((height, index) => {
            const barProgress = ((index + 1) / waveformBars.length) * 100;
            const isFilled = progress >= barProgress;

            return (
              <span
                key={index}
                style={{
                  width: "3px",
                  height: `${height}px`,
                  borderRadius: "999px",
                  backgroundColor: isFilled ? "#1677ff" : "#d1d5db",
                  transition: "background-color 0.15s ease",
                }}
              />
            );
          })}
        </div>

        <div
          style={{
            minWidth: "38px",
            textAlign: "right",
            fontSize: "11px",
            color: "#6b7280",
            fontWeight: 600,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {formatAudioTime(isPlaying ? currentTime : duration || currentTime)}
        </div>
      </div>
    </div>
  );
};

const SingleChatItem = ({
  message: msng,
  convIndex,
  msgIndex,
  setVisible,
  setResponseData,
  setDrawerData,
  isSelected = false,
  onToggleSelect,
  selectionGroupIds = [],
  selectionGroupPosition = "single",
  hasActiveSelection = false,
}) => {
  const dispatch = useAppDispatch();
  const isAutomaticMessage =
    msng?.role === "automatic" || msng?.sender === "Automatic";

  // State for like/dislike buttons - initialize based on evaluation_status
  const [liked, setLiked] = useState(msng?.evaluation_status === "like");
  const [disliked, setDisliked] = useState(
    msng?.evaluation_status === "dislike"
  );
  const [feedbackModalVisible, setFeedbackModalVisible] = useState(false);

  // Extract images from message if they exist
  const images = msng.images || [];
  const hasMedia = msng?.media_ids && msng?.media_ids?.length > 0;

  const getMediaUrl = (media) => {
    if (typeof media === "string") return media;
    return media?.url || media?.media_url || "";
  };

  const isUrlLikeMedia = (url = "") => /^(https?:|blob:|data:)/i.test(url);

  const isAudioMedia = (media) => {
    const url = getMediaUrl(media);
    const mimeType = media?.type || media?.mime_type || media?.content_type || "";

    return (
      mimeType.startsWith("audio/") ||
      /\.(webm|m4a|mp3|wav|ogg|aac)(\?|#|$)/i.test(url)
    );
  };

  const isImageMedia = (media) => {
    const url = getMediaUrl(media);
    const mimeType = media?.type || media?.mime_type || media?.content_type || "";

    return (
      mimeType.startsWith("image/") ||
      /^data:image\//i.test(url) ||
      /\.(jpg|jpeg|png|gif|bmp|webp|svg)(\?|#|$)/i.test(url) ||
      (typeof media === "string" && isUrlLikeMedia(url) && !isAudioMedia(media))
    );
  };

  const getMediaLabel = (media, index) => {
    if (typeof media === "string") return `Attachment ${index + 1}`;
    return media?.name || media?.filename || `Attachment ${index + 1}`;
  };

  const renderMediaItems = (mediaIds) => {
    const mediaItems = Array.isArray(mediaIds) ? mediaIds : [mediaIds];
    const audioItems = mediaItems.filter(isAudioMedia);
    const visualItems = mediaItems.filter((media) => !isAudioMedia(media) && isImageMedia(media));
    const unsupportedItems = mediaItems.filter((media) => !isAudioMedia(media) && !isImageMedia(media));

    if (
      mediaItems.length === 1 &&
      typeof mediaItems[0] === "string" &&
      !isUrlLikeMedia(mediaItems[0])
    ) {
      return (
        <Card style={{ backgroundColor: isSelected ? "#c6c6c6" : "#f0f0f0" }}>
          {mediaItems[0]}
        </Card>
      );
    }

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        {visualItems.length > 0 && <RenderImageCollage images={visualItems} />}
        {audioItems.map((media, index) => {
          const url = getMediaUrl(media);
          if (!url) return null;

          return (
            <div
              key={`${url}-${index}`}
            >
              <VoiceMessagePlayer src={url} isSelected={isSelected} />
            </div>
          );
        })}
        {unsupportedItems.map((media, index) => (
          <div
            key={`${getMediaLabel(media, index)}-${index}`}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              width: "fit-content",
              maxWidth: "280px",
              padding: "8px 10px",
              backgroundColor: isSelected ? "#c6c6c6" : "#fff7e6",
              border: "1px solid #ffd591",
              borderRadius: "8px",
              color: "#ad6800",
              fontSize: "12px",
              fontWeight: 500,
            }}
          >
            <iconify-icon icon="mdi:file-alert-outline" width="16" height="16" />
            <span
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={getMediaLabel(media, index)}
            >
              Unsupported attachment
            </span>
          </div>
        ))}
      </div>
    );
  };

  // State for feedback modal
  const [form] = Form.useForm();

  // Split message content by double newlines for assistant messages
  const messageParts =
    msng?.role === "assistant" && msng?.content
      ? msng?.content
        .replace(/\\n/g, "") // Unescape newlines
        .replace(/\\n{2}/g, " \n") // Exactly 2 newlines → space
        .split(/\n{3,}/) // Split on 3+ newlines
        .filter((part) => part.trim())
      : [];
  const aiActionsCount = Array.isArray(msng?.information_gathering)
    ? msng.information_gathering.length
    : 0;
  const hasAiActions = aiActionsCount > 0;
  const queryFinding = msng?.query_finding || msng?.query_findings;
  const canSelectMessage = selectionGroupIds.length > 0;

  const messageContextMenu = canSelectMessage
    ? {
      items: [
        {
          key: "toggle_select",
          label: isSelected ? "Unselect message" : "Select message",
        },
      ],
      onClick: ({ key, domEvent }) => {
        if (key !== "toggle_select") return;
        domEvent.preventDefault();
        domEvent.stopPropagation();
        onToggleSelect?.(selectionGroupIds);
      },
    }
    : null;

  const renderSelectableWrapper = (content) => {
    if (!messageContextMenu) return content;
    return (
      <Dropdown trigger={["contextMenu"]} menu={messageContextMenu}>
        <div>{content}</div>
      </Dropdown>
    );
  };

  const handleSelectionModeClick = (event) => {
    if (!hasActiveSelection || !canSelectMessage) return;
    event.preventDefault();
    event.stopPropagation();
    onToggleSelect?.(selectionGroupIds);
  };

  const handleCheckboxSelectChange = (event) => {
    if (!canSelectMessage) return;
    event.preventDefault();
    event.stopPropagation();
    onToggleSelect?.(selectionGroupIds);
  };

  const renderSelectCheckbox = () => {
    if (!canSelectMessage) return null;
    return (
      <Tooltip title={isSelected ? "Unselect message" : "Select message"}>
        <Checkbox
          checked={isSelected}
          onClick={(event) => {
            event.stopPropagation();
          }}
          onChange={handleCheckboxSelectChange}
          style={{
            marginTop: "4px",
            transform: "scale(0.85)",
          }}
        />
      </Tooltip>
    );
  };

  const getSelectedAvatarProps = (defaultIcon, defaultStyle = {}) => ({
    icon: defaultIcon,
    style: defaultStyle,
  });

  // Handle like button click
  const handleLike = (e) => {
    e.stopPropagation(); // Prevent bubble click event

    // If already liked, undo the like
    if (liked) {
      dispatch(
        handleFeedbackDeleteApi({
          data: {
            message_id: msng?.message_id,
          },
          finalCallback: () => { },
          successCallback: (response) => {
            setLiked(false);
            console.log("Like removed -> ", msng);
            message.warning("Unliked!");
          },
          failureCallback: () => { },
        })
      );
      return;
    }

    dispatch(
      handleFeedbackApi({
        data: {
          message_id: msng?.message_id,
          evaluation: "like",
          remark: null,
          categories: null,
          reviewer_id: localStorage.getItem("userId"),
          created_at: null,
        },
        finalCallback: () => { },
        successCallback: (response) => {
          console.log("Message liked -> ", msng);
          setLiked(true);
          if (disliked) setDisliked(false);
          message.success("Liked!");
        },
        failureCallback: () => { },
      })
    );
  };

  // Handle dislike button click
  const handleDislike = (e) => {
    e.stopPropagation(); // Prevent bubble click event

    // If already disliked, just undo it without showing modal
    if (disliked) {
      dispatch(
        handleFeedbackDeleteApi({
          data: {
            message_id: msng?.message_id,
          },
          finalCallback: () => { },
          successCallback: (response) => {
            setDisliked(false);
            console.log("Dislike removed:", msng);
            message.warning("Dislike removed!");
          },
          failureCallback: () => { },
        })
      );
      return;
    }

    setFeedbackModalVisible(true);
    console.log("Message disliked:", msng);
  };

  // Function to check if a URL is a YouTube link and extract video ID
  const getYouTubeVideoId = (url) => {
    const youtubeRegex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
    const match = url.match(youtubeRegex);
    return match ? match[1] : null;
  };

  // Function to render media from reply_urls
  const renderReplyUrls = (urls) => {
    if (!urls || urls.length === 0) return null;

    // Filter out invalid URLs
    const validUrls = filterValidUrls(urls);

    if (validUrls.length === 0) return null;

    return (
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
        {validUrls.map((url, index) => {
          const videoId = getYouTubeVideoId(url);
          if (videoId) {
            // Render YouTube thumbnail
            return (
              <a
                key={index}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Image
                  src={`https://img.youtube.com/vi/${videoId}/hqdefault.jpg`}
                  alt="YouTube video thumbnail"
                  style={{
                    width: 120,
                    height: 90,
                    objectFit: "cover",
                    borderRadius: 4,
                  }}
                  preview={false}
                  fallback="https://via.placeholder.com/120x90?text=Video+Unavailable"
                />
              </a>
            );
          } else {
            // Render image
            return (
              <Image
                key={index}
                src={url}
                alt="Attached image"
                style={{
                  maxWidth: 200,
                  maxHeight: 200,
                  objectFit: "contain",
                  borderRadius: 4,
                }}
                fallback="https://via.placeholder.com/200x200?text=Image+Not+Found"
              />
            );
          }
        })}
      </div>
    );
  };

  return renderSelectableWrapper(
    <div
      key={`conv-${convIndex}-msg-${msgIndex}`}
      style={{
        marginBottom: "16px",
        position: "relative",
        cursor: hasActiveSelection && canSelectMessage ? "pointer" : "default",
      }}
      onClick={handleSelectionModeClick}
    >
      {isAutomaticMessage ? (
        <div>
          <Space align="start">
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <Avatar
                {...getSelectedAvatarProps(
                  <RobotOutlined />,
                  { backgroundColor: "#8c8c8c" }
                )}
              />
              {renderSelectCheckbox()}
            </div>
            <div
              style={{
                backgroundColor: isSelected ? "#c9c9c9" : "#f5f5f5",
                borderRadius: "8px",
                padding: "12px",
                maxWidth: "80%",
                border: "1px solid #e8e8e8",
              }}
            >
              <Text
                style={{
                  display: "block",
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "#595959",
                  marginBottom: "4px",
                }}
              >
                Automatic
              </Text>
              <Paragraph style={{ marginBottom: 0 }}>
                {parseReplyText(msng?.content || "")}
              </Paragraph>
            </div>
          </Space>

          <div style={{ marginLeft: "45px", marginTop: "4px" }}>
            <Text type="secondary" style={{ fontSize: "12px" }}>
              {formatTime(msng?.created_at || new Date().toISOString())}
            </Text>
          </div>
        </div>
      ) : msng?.role === "assistant" ? (
        <Space align="start" direction="vertical" style={{ width: "100%" }}>
          <Space align="start">
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <Avatar
                {...getSelectedAvatarProps(
                  <RobotOutlined />,
                  { backgroundColor: "#1890ff" }
                )}
              />
              {renderSelectCheckbox()}
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* Map through message parts to create multiple bubbles */}
              {messageParts?.length > 0 ? (
                messageParts?.map((part, partIndex) => (
                  <div
                    key={partIndex}
                    style={{
                      backgroundColor: isSelected ? "#9fcbff" : "#e6f7ff",
                      borderRadius: "8px",
                      padding: "12px",
                      maxWidth: "80%",
                      cursor: "pointer",
                    }}
                    onClick={(event) => {
                      if (hasActiveSelection && canSelectMessage) {
                        event.preventDefault();
                        event.stopPropagation();
                        onToggleSelect?.(selectionGroupIds);
                        return;
                      }
                      setDrawerData(msng);
                      setVisible(true);
                      setResponseData({ ...msng, content: part });
                    }}
                  >
                    <Paragraph style={{ marginBottom: 0 }}>
                      {parseReplyText(part)}
                    </Paragraph>
                  </div>
                ))
              ) : (
                // Fallback for empty content or no double newlines
                <div
                  style={{
                    backgroundColor: isSelected ? "#9fcbff" : "#e6f7ff",
                    borderRadius: "8px",
                    padding: "12px",
                    minHeight: "46px",
                    maxWidth: "80%",
                    cursor: "pointer",
                  }}
                  onClick={(event) => {
                    if (hasActiveSelection && canSelectMessage) {
                      event.preventDefault();
                      event.stopPropagation();
                      onToggleSelect?.(selectionGroupIds);
                      return;
                    }
                    setDrawerData(msng);
                    setVisible(true);
                    setResponseData(msng);
                  }}
                >
                  <Paragraph style={{ marginBottom: 0 }}>
                    {msng?.content ? (
                      parseReplyText(msng.content)
                    ) : (
                      <span style={{ visibility: "hidden" }}>.</span>
                    )}
                  </Paragraph>
                </div>
              )}
            </div>
          </Space>

          {/* Render reply_urls in a separate container below the message */}
          {msng?.reply_urls?.length > 0 && (
            <div style={{ marginLeft: "45px", marginTop: "8px" }}>
              {renderReplyUrls(msng?.reply_urls)}
            </div>
          )}

          {/* Render metadata images if they exist */}
          {msng?.metadata?.length > 0 &&
            msng?.metadata?.some((item) => item?.image_urls?.length > 0) && (
              <div style={{ marginLeft: "45px", marginTop: "8px" }}>
                {msng?.metadata?.map(
                  (item, index) =>
                    item?.image_urls?.length > 0 && (
                      <RenderImageCollage
                        key={index}
                        images={item?.image_urls?.map((img) => img)}
                      />
                    )
                )}
              </div>
            )}

          {/* Timestamp and processing time */}
          <div
            style={{
              marginLeft: "45px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flexWrap: "wrap",
            }}
          >
            <Space style={{ marginLeft: 0 }}>
              <Tooltip title="Like">
                <Button
                  type={liked ? "primary" : "text"}
                  size="small"
                  icon={liked ? <LikeFilled /> : <LikeOutlined />}
                  onClick={handleLike}
                />
              </Tooltip>

              <Tooltip title="Dislike">
                <Button
                  type={disliked ? "primary" : "text"}
                  size="small"
                  danger={disliked}
                  icon={disliked ? <DislikeFilled /> : <DislikeOutlined />}
                  onClick={handleDislike}
                />
              </Tooltip>
            </Space>
            {hasAiActions && (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "4px",
                  padding: "2px 8px",
                  borderRadius: "999px",
                  backgroundColor: "#e6f4ff",
                  border: "1px solid #91caff",
                  color: "#0958d9",
                  fontSize: "11px",
                  fontWeight: 500,
                  lineHeight: 1,
                }}
              >
                <iconify-icon
                  icon="mdi:robot-outline"
                  width="12"
                  height="12"
                  style={{ color: "#1677ff", display: "block", flexShrink: 0 }}
                ></iconify-icon>
                {`${aiActionsCount} AI action${aiActionsCount > 1 ? "s" : ""}`}
              </span>
            )}
            <QueryFindingInfoButton queryFinding={queryFinding} />
            <Text
              type="secondary"
              style={{
                textAlign: "right",
                fontSize: "12px",
              }}
            >
              {formatTime(msng?.created_at || new Date().toISOString())}
              <Tooltip title={`It took ${(msng?.processing_time || 0).toFixed(2)} seconds to generate this response.`}>
                {` · ${(msng?.processing_time || 0).toFixed(2)}s`}
              </Tooltip>
            </Text>
          </div>
        </Space>
      ) : (
        <div>
          <div
            style={{
              ...styles.userMessageContainer,
              flexDirection: "column",
              alignItems: "flex-end",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "flex-start",
              }}
            >
              {msng?.content ? (
                <>
                  <div
                    style={{
                      ...styles.userMessageContent,
                      backgroundColor: isSelected
                        ? "rgb(150, 200, 102)"
                        : styles.userMessageContent.backgroundColor,
                      display: "flex",
                      flexDirection: "column",
                      gap: msng?.ads_flag && msng?.simulated_automatic_message ? "8px" : "0px"
                    }}
                  >
                    {msng?.ads_flag && msng?.simulated_automatic_message && (
                      <div
                        style={{
                          backgroundColor: "rgba(0, 0, 0, 0.06)",
                          borderLeft: "4px solid #2e8b57",
                          padding: "6px 10px",
                          borderRadius: "4px",
                          fontSize: "12px",
                          lineHeight: "1.4",
                          maxWidth: "100%",
                          textAlign: "left"
                        }}
                      >
                        <div style={{ fontWeight: 600, color: "#2e8b57", marginBottom: "2px", display: "flex", alignItems: "center", gap: "4px" }}>
                          <iconify-icon icon="mdi:bullhorn-outline" width="14" height="14"></iconify-icon>
                          Replied to Ad
                        </div>
                        <div style={{
                          display: "-webkit-box",
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          color: "#555"
                        }}>
                          {msng.simulated_automatic_message}
                        </div>
                      </div>
                    )}
                    <Paragraph style={{ marginBottom: 0 }}>
                      {msng?.content}
                    </Paragraph>
                  </div>
                </>
              ) : !hasMedia && !msng?.message_id ? (
                <>
                  <div style={{ marginBottom: "16px" }}>
                    <div
                      className="message-loading"
                      style={{
                        backgroundColor: "rgb(220, 245, 200)",
                      }}
                    >
                      <div className="bouncing-dots">
                        <span
                          style={{ backgroundColor: "rgb(46, 139, 87)" }}
                        ></span>
                        <span
                          style={{ backgroundColor: "rgb(46, 139, 87)" }}
                        ></span>
                        <span
                          style={{ backgroundColor: "rgb(46, 139, 87)" }}
                        ></span>
                      </div>
                    </div>
                  </div>
                </>
              ) : hasMedia ? (
                /* Render media_ids inline when there's no content */
                <div style={{ maxWidth: "80%", marginRight: "0px" }}>
                  {renderMediaItems(msng?.media_ids)}
                </div>
              ) : null}

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  marginLeft: "8px",
                }}
              >
                <Avatar
                  {...getSelectedAvatarProps(
                    <UserOutlined />,
                    {
                      backgroundColor: "#87d068",
                    }
                  )}
                />
                {renderSelectCheckbox()}
              </div>
            </div>

            {/* Render media_ids below when there's also content */}
            {hasMedia && msng?.content && (
              <div style={{ marginTop: "8px", maxWidth: "80%", marginRight: "38px" }}>
                {renderMediaItems(msng?.media_ids)}
              </div>
            )}
          </div>

          {/* Render reply_urls in a separate container below the message */}
          {msng?.reply_urls?.length > 0 && (
            <div style={{ marginTop: "8px", marginRight: "30px", textAlign: "right" }}>
              {renderReplyUrls(msng?.reply_urls)}
            </div>
          )}

          {/* Timestamp */}
          <div
            style={{
              marginTop: "4px",
              textAlign: "right",
              marginRight: "30px",
            }}
          >
            <Text type="secondary" style={{ fontSize: "12px" }}>
              {formatTime(msng?.created_at || new Date().toISOString())}
            </Text>
          </div>
        </div>
      )}

      {msng?.role === "assistant" && (
        <FeedbackModal
          message={msng}
          feedbackModalVisible={feedbackModalVisible}
          setFeedbackModalVisible={setFeedbackModalVisible}
          liked={liked}
          setLiked={setLiked}
          setDisliked={setDisliked}
        />
      )}
    </div>
  );
};

export default SingleChatItem;
